import { useParams, Link } from "react-router-dom";
import { getComicsByCategory, getCategoryBySlug, categories } from "@/mocks/comics";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import NotFound from "@/pages/NotFound";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || "all");

  if (!category) return <NotFound />;

  const comicsList = getComicsByCategory(slug || "all");
  const categoryTitle = category.slug === "all" ? "漫画大全" : `${category.name}漫画`;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${categoryTitle} - 韩国漫画免费在线观看 - 哈哈漫画网`}
        description={`哈哈漫画网${categoryTitle}专区，提供高清${category.name}韩漫全集下拉式阅读。收录大量优质${category.name}类韩国漫画，每日更新，完全免费。`}
        keywords={`${category.name}漫画,韩国${category.name}漫画,${category.name}韩漫,免费${category.name}漫画,哈哈漫画网`}
        canonicalUrl={`https://hahacomic.com/category/${slug}`}
        ogType="website"
        schemaType="WebPage"
      />
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: categoryTitle },
            ]}
          />
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
            {categoryTitle}
          </h1>
          <p className="text-sm text-foreground-500 mb-6">共 {comicsList.length} 部作品</p>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  (slug === cat.slug || (slug === undefined && cat.slug === "all"))
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {comicsList.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5" data-product-shop="true">
              {comicsList.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - ${category.name}韩国漫画免费在线观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-full font-medium">
                        {comic.rating}
                      </span>
                    </div>
                    {comic.status === "已完结" && (
                      <div className="absolute top-2 right-2">
                        <span className="bg-foreground-950/75 text-background-50 text-xs px-2 py-0.5 rounded-full">
                          已完结
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1 truncate">{comic.author}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-foreground-400">
                      <i className="ri-eye-line mr-0.5"></i>
                      {formatViews(comic.views)}
                    </span>
                    <span className="text-xs text-foreground-400">
                      共{comic.chapters.length}话
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-foreground-500">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

function formatViews(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(n % 10000 === 0 ? 0 : 1)}万`;
  return String(n);
}